import { useEffect, useMemo } from 'react';

import Image from 'next/image';

import CryptoHeadButton from '@/components/dashboard/crypto-prices/CryptoHeadButton';
import TableUi from '@/components/dashboard/top-sales/TableUi';
import LeaderMobileCard from '@/components/rewards/leader-mobile-card';
import RewardCard from '@/components/rewards/reward-card';
import { useLeaderboard } from '@/hooks/query/loans/useLeaderboard';
import useAuthenticate from '@/hooks/useAuthenticate';
import { useTheme } from '@/hooks/useTheme';
import { truncateAddress } from '@/utils/formatter';
import { AWSImageConverter, cn } from '@/utils/helper';
import { TLoanTableHeaders } from 'types/loan';

const pointsHead = {
  label: <span className='hidden lg:flex  font-space-mono font-bold text-2xl lg:text-base text-white'>Points</span>,
  value: 'point',
  className: 'flex lg:justify-center font-bold text-xl lg:text-base font-mono min-w-[136px]',
};

const borrowingHeads = [
  {
    label: 'Borrowing',
    value: 'borrow',
    className: 'font-medium text-base font-poppins',
  },
  pointsHead,
];

const borrowingData = [
  {
    borrow: 'Borrow $1000 to $2500',
    point: 150,
  },
  {
    borrow: 'Borrow $2500 to $5000',
    point: 300,
  },
  {
    borrow: 'Borrow $5000 to $10000',
    point: 600,
  },
  {
    borrow: 'Borrow $10000 to $20000',
    point: 1200,
  },
  {
    borrow: 'Borrow above $20000',
    point: 2000,
  },
];

const paybackHeads = [
  {
    label: 'Payback Loan',
    value: 'payback',
  },
  pointsHead,
];

const paybackData = [
  {
    payback: 'Payback $1000 to $2500',
    point: 150,
  },
  {
    payback: 'Payback $2500 to $5000',
    point: 300,
  },
  {
    payback: 'Payback $5000 to $10000',
    point: 600,
  },
  {
    payback: 'Payback $10000 to $20000',
    point: 1200,
  },
  {
    payback: 'Payback above $20000',
    point: 2000,
  },
];

const lendingHeads = [
  {
    label: 'Lending',
    value: 'lend',
  },
  pointsHead,
];

const lendingData = [
  {
    lend: 'Lend $1000 to $2500',
    point: 150,
  },
  {
    lend: 'Lend $2500 to $5000',
    point: 300,
  },
  {
    lend: 'Lend $5000 to $10000',
    point: 600,
  },
  {
    lend: 'Lend $10000 to $20000',
    point: 1200,
  },
  {
    lend: 'Lend above $20000',
    point: 2000,
  },
];

const refinanceHeads = [
  {
    label: 'Refinance loan',
    value: 'refinance',
  },
  pointsHead,
];

const refinanceData = [
  {
    refinance: 'Refinance $1000 to $2500',
    point: 150,
  },
  {
    refinance: 'Refinance $2500 to $5000',
    point: 300,
  },
  {
    refinance: 'Refinance $5000 to $10000',
    point: 600,
  },
  {
    refinance: 'Refinance $10000 to $20000',
    point: 1200,
  },
  {
    refinance: 'Refinance above $20000',
    point: 2000,
  },
];

const leaderBoardColumns: TLoanTableHeaders = [
  {
    Header: (props) => {
      return <CryptoHeadButton disableSorting columnName='Position' {...props} />;
    },
    accessor: 'position',
    disableSortBy: true,
    thClassName: 'rounded-tl-[2px] rounded-bl-[2px] w-0 pr-6 !pl-[55px]',
    className: 'pl-[58px]',
  },
  {
    Header: (props) => <CryptoHeadButton columnName='Name' {...props} />,
    accessor: 'name',
    sortId: 'user.name',
    thClassName: 'w-0 pr-32',
  },
  {
    Header: (props) => <CryptoHeadButton columnName='Borrowing Points' {...props} />,
    accessor: 'borrowingPoints',
    sortId: 'borrowPoints',
    disableSortBy: true,
    thClassName: 'w-0 pr-10 ',
  },
  {
    Header: (props) => <CryptoHeadButton columnName='Payback Points' {...props} />,
    accessor: 'paybackPoints',
    sortId: 'paybackPoints',
    disableSortBy: true,
    thClassName: 'w-0 pr-10 ',
  },
  {
    Header: (props) => <CryptoHeadButton columnName='Lending Points' {...props} />,
    accessor: 'lendingPoints',
    sortId: 'lendingPoints',
    disableSortBy: true,
    thClassName: 'w-0 pr-10 ',
  },
  {
    Header: (props) => <CryptoHeadButton columnName='Refinance Points' {...props} />,
    accessor: 'refinancePoints',
    sortId: 'refinancePoints',
    disableSortBy: true,
    thClassName: 'w-0 ',
  },
  {
    Header: (props) => <CryptoHeadButton columnName='Total Points' {...props} />,
    accessor: 'totalPoints',
    sortId: 'totalPoints',
    disableSortBy: true,
    thClassName: 'w-0 px-8 rounded-tr-[2px] rounded-br-[2px] text-center',
    thPClassName: 'm-auto',
    className: 'px-8 text-center !text-forest-green font-bold',
  },
];

function Rewards() {
  const { isAuth } = useAuthenticate();
  const { handleSetTheme } = useTheme();

  const { data: leaderBoardData, isLoading, fetchNextPage, hasNextPage } = useLeaderboard();

  useEffect(() => {
    handleSetTheme('dark');
  }, [handleSetTheme]);

  const leaderBoardTableData = useMemo(() => {
    if (isLoading || !leaderBoardData) return [];

    return leaderBoardData.pages
      .flatMap((page) => page)
      .flatMap((page) => page.rows)
      .map((obj, idx) => {
        return {
          name: (
            <div className='flex gap-3 items-center'>
              <Image alt='avatar' width={38} height={38} className='h-9' src={obj.user.profile_picture ? AWSImageConverter(obj.user.profile_picture) : '/avatar-placeholder.png'} />
              <div className='font-poppins flex flex-col'>
                <span className='text-base font-medium'>{obj.user.name}</span>
                <span>{truncateAddress(obj.walletAddress)}</span>
              </div>
            </div>
          ),
          borrowingPoints: obj.borrowPoints || 'N/A',
          paybackPoints: obj.paybackPoints || 'N/A',
          lendingPoints: obj.lendingPoints || 'N/A',
          refinancePoints: obj.refinancePoints || 'N/A',
          totalPoints: obj.totalPoints || 'N/A',
          position: idx + 1,
        };
      });
  }, [leaderBoardData, isLoading]);

  return (
    <div className={cn('mt-14 pb-28 bg-white dark:bg-jet-black text-coal-250 dark:text-white', isAuth ? 'px-[26px] lg:px-20' : 'mx-auto px-1 ss:px-3 md:px-12')}>
      <h1 className='text-[38px] lg:text-[52px]/[72px] font-semibold text-center leading-[41px]'>Rewards</h1>
      <p className='text-base mt-4 lg:mt-0 leading-[21px] lg:leading-[38px] text-center font-medium'>
        $Netty will be air dropped to the NettyWorth community at the end of S1.{' '}
        <a className='dark:!text-primary-blue-100 text-primary-blue-600' href='https://docs.nettyworth.io/docs/tokenomics' target='_blank'>
          Learn More
        </a>
      </p>

      <div className='lg:mt-[57px] mt-[50px] grid lg:grid-cols-2 gap-5'>
        <RewardCard data={borrowingData} heads={borrowingHeads} />
        <RewardCard data={paybackData} heads={paybackHeads} />
        <RewardCard data={lendingData} heads={lendingHeads} />
        <RewardCard data={refinanceData} heads={refinanceHeads} />
      </div>
      <div className='flex flex-col'>
        <h2 className='lg:text-[52px] text-[38px] font-semibold text-center lg:my-20 mt-11 mb-6 leading-[48px]'>NettyWorth Leaderboard</h2>
        <TableUi enableDefaultSort thTextClassName='text-white' wrapperStyle='border-t border-x dark:border-coal-border rounded-[3px] hidden lg:block' data={leaderBoardTableData} columns={leaderBoardColumns} thStyle='bg-primary py-[22px] border-b border-coal-border pl-0 text-white' tdStyle='font-space-mono py-[22px]' sortIconClassName='w-[4px] h-[3px]' />
        <div className='lg:hidden flex flex-col gap-5'>
          {leaderBoardData?.pages
            .flatMap((page) => page)
            .flatMap((page) => page.rows)
            .map((item, index) => (
              <LeaderMobileCard
                key={index}
                position={index + 1}
                name={item.user.name}
                paybackPoints={item.paybackPoints}
                address={item.walletAddress}
                avatar={item.user.profile_picture ? AWSImageConverter(item.user.profile_picture) : '/avatar-placeholder.png'}
                borrowPoints={item.borrowPoints}
                lendingPoints={item.lendingPoints}
                refinancePoints={item.refinancePoints}
                totalPoints={item.totalPoints}
              />
            ))}
        </div>
        {(isLoading || hasNextPage) && (
          <button disabled={isLoading} className='border border-coal-border rounded mt-9 m-auto px-8 py-3' onClick={() => fetchNextPage()}>
            {isLoading ? 'Loading...' : hasNextPage ? 'Load More' : 'No more data'}
          </button>
        )}
      </div>
    </div>
  );
}

Rewards.generalLayoutClassName = '!bg-jet-black';
Rewards.hideSocialAndGas = false;
Rewards.hideFooter = true;
Rewards.customStyle = {
  container: 'overflow-auto custom-scrollbar !pb-14',
};
Rewards.enableMobile = true;

export default Rewards;
